import { Button as CTATest, theme } from '@prose-ui';
import { styled } from '@prose-ui/legacy';

import { Button } from 'Components/LegacyButton';

import { useCheckoutCtaAbTest } from 'abTesting/checkoutCta/hook';

import type { CtaButtonProps } from '../Blocks/CheckoutTicket';

import { handleKeyPress } from 'utils/a11y';

import * as flagVariants from 'dux/featureFlags/constants/flagVariants';

import CheckoutSummaryHeader from './OrderSummarHeader';
import { OrderSummaryPromoCommunication } from './OrderSummaryPromoCommunication';

const LowercaseOrderSummaryPromoCommunication = styled(OrderSummaryPromoCommunication)`
  text-transform: lowercase;
`;

const MobileFooter = styled.div<{ padding: boolean }>`
  position: fixed;
  bottom: 0;
  left: 0;

  display: flex;
  flex-direction: column;
  width: 100%;
  padding: ${({ padding }) => (padding ? `${theme.spacing['3x']} ${theme.spacing['4x']}` : 0)};
  background-color: ${theme.colors.neutral[100]};
  box-shadow: 0 0 6px 0 rgb(0 0 0 / 15%);
`;

const CTAControl = styled(Button)`
  position: relative;

  margin: 0;
`;

const Drawer = styled.div<{ padding?: boolean }>`
  display: flex;
  flex-direction: column;
  padding: ${({ padding }) => (padding ? '12px 16px' : 0)};
`;

const Divider = styled.span`
  width: 100%;
  height: 1px;
  margin: ${theme.spacing['3x']} 0 ${theme.spacing['3x']};
  background-color: ${theme.colors.neutral[600]};
`;

type CartMobileFooterProps = {
  ctaButtonProps?: CtaButtonProps;
  onToggleCart: () => void;
};

export const CartMobileFooter = ({ ctaButtonProps, onToggleCart }: CartMobileFooterProps) => {
  const { variantName: checkoutCtaVariantName } = useCheckoutCtaAbTest({
    isTrackingOnMount: false,
  });

  return (
    <MobileFooter padding={checkoutCtaVariantName === flagVariants.VARIANT1}>
      {checkoutCtaVariantName === flagVariants.VARIANT1 && (
        <>
          <Drawer
            aria-label="Open checkout ticket modal"
            data-click="order-summary-open"
            data-from="cart"
            data-testid="cart-order-summary-drawer"
            onClick={onToggleCart}
            onKeyPress={handleKeyPress(onToggleCart)}
            role="button"
            tabIndex={0}
          >
            <CheckoutSummaryHeader chevronDirection="down" />
          </Drawer>

          {ctaButtonProps && (
            <>
              <Divider />
              {/* TODO: rename component when checkout-cta-ab-test is over */}
              <CTATest
                capsule
                data-testid="mobile-footer-checkout-ticket-button"
                disabled={ctaButtonProps.disabled}
                isLoading={ctaButtonProps.isLoading}
                onClick={ctaButtonProps.onClick}
                size="full"
                style={{
                  marginBottom: '4px',
                  width: '100%',
                }}
                type={ctaButtonProps.type}
                variant={ctaButtonProps.variant}
              >
                {ctaButtonProps.text}
              </CTATest>
            </>
          )}
          <LowercaseOrderSummaryPromoCommunication />
        </>
      )}

      {checkoutCtaVariantName !== flagVariants.VARIANT1 && (
        <>
          <Drawer
            aria-label="Open checkout ticket modal"
            data-click="order-summary-open"
            data-from="cart"
            data-testid="cart-order-summary-drawer"
            onClick={onToggleCart}
            onKeyPress={handleKeyPress(onToggleCart)}
            padding
            role="button"
            tabIndex={0}
          >
            <CheckoutSummaryHeader chevronDirection="down" />
            <LowercaseOrderSummaryPromoCommunication />
          </Drawer>

          {ctaButtonProps && (
            <CTAControl
              data-testid="mobile-footer-checkout-ticket-button"
              disabled={ctaButtonProps.disabled}
              fullWidth
              isLoading={ctaButtonProps.isLoading}
              onClick={ctaButtonProps.onClick}
              type={ctaButtonProps.type}
              // @ts-expect-error - ctaButtonProps.variant type too broad
              variant={ctaButtonProps.variant}
            >
              {ctaButtonProps.text}
            </CTAControl>
          )}
        </>
      )}
    </MobileFooter>
  );
};
